/* eslint-disable jsx-a11y/control-has-associated-label */
import { EditorState, Modifier, RichUtils } from "draft-js";
import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import { faImage } from "@fortawesome/free-regular-svg-icons";
import { faBold, faCode, faItalic, faListOl, faListUl, faPaste, faRepeat, faUnderline, faUndo } from "@fortawesome/free-solid-svg-icons";

const editorButtons = [
  { type: "block", style: "header-one", title: "H1" },
  { type: "block", style: "header-two", title: "H2" },
  { type: "block", style: "header-three", title: "H3" },
  { type: "block", style: "header-four", title: "H4" },
  { type: "block", style: "header-five", title: "H5" },
  { type: "block", style: "header-six", title: "H6" },
  { type: "block", style: "unstyled", title: "P" },
  { type: "block", style: "blockquote", title: "BLK" },
  {
    type: "block",
    style: "unordered-list-item",
    title: <FontAwesomeIcon icon={faListUl} />,
  },
  {
    type: "block",
    style: "ordered-list-item",
    title: <FontAwesomeIcon icon={faListOl} />,
  },
  {
    type: "block",
    style: "code-block",
    title: <FontAwesomeIcon icon={faCode} />,
  },
  { type: "separator" },
  {
    type: "inline",
    style: "BOLD",
    title: <FontAwesomeIcon icon={faBold} />,
  },
  {
    type: "inline",
    style: "ITALIC",
    title: <FontAwesomeIcon icon={faItalic} />,
  },
  {
    type: "inline",
    style: "UNDERLINE",
    title: <FontAwesomeIcon icon={faUnderline} />,
  },
  {
    type: "inline",
    style: "CODE",
    title: <FontAwesomeIcon icon={faCode} />,
  },
  { type: "separator" },
];

function EditorButtons({ editorState, setEditorState, insertImage }) {
  const checkIsButtonActive = (button) =>
    // check if the selected text has this inline style and set the button active
    editorState.getCurrentInlineStyle().has(button.style) ||
    // check if the selected text has this block style and set the button active
    editorState
      .getCurrentContent()
      .getBlockForKey(editorState.getSelection().getStartKey())
      .getType() === button.style;

  // inserts (Paste) text without formatting
  const insertWithoutFormat = () => {
    navigator.clipboard.readText().then(
      (clipText) => {
        const contentStateWithPaste = Modifier.replaceText(
          editorState.getCurrentContent(),
          editorState.getSelection(),
          clipText
        );

        const newEditorState = EditorState.set(editorState, {
          currentContent: contentStateWithPaste,
        });
        setEditorState(newEditorState);
      },
      (e) => {
        toast.error(`Could not read the clipboard contents.${e}`);
      }
    );
  };

  return (
    <div>
      {editorButtons.map((button) =>
        button.type === "separator" ? (
          " | "
        ) : (
          <button
            key={button.style}
            type="button"
            title={button.style}
            tabIndex={-1}
            // check if the button is active
            className={`btn btn-outline-secondary border-0 btn-sm
                            ${checkIsButtonActive(button) ? "active" : ""}
                        `}
            onClick={() => {
              const selection = editorState.getSelection();
              // set block style
              if (button.type === "block") {
                setEditorState(
                  RichUtils.toggleBlockType(
                    EditorState.forceSelection(editorState, selection),
                    button.style
                  )
                );
              }
              // set inline style
              if (button.type === "inline") {
                setEditorState(
                  RichUtils.toggleInlineStyle(
                    EditorState.forceSelection(editorState, selection),
                    button.style
                  )
                );
              }
            }}
          >
            {button.title}
          </button>
        )
      )}
      <button
        type="button"
        className="btn btn-outline-secondary border-0 btn-sm"
        tabIndex={-1}
        onClick={() =>
          setEditorState((previousState) => EditorState.undo(previousState))
        }
        disabled={editorState.getUndoStack().size === 0}
        title={
          editorState.getUndoStack().size === 0 ? "Nothing to undo" : "Undo"
        }
      >
        <FontAwesomeIcon icon={faUndo} />
      </button>
      <button
        type="button"
        className="btn btn-outline-secondary border-0 btn-sm"
        tabIndex={-1}
        onClick={() =>
          setEditorState((previousState) => EditorState.redo(previousState))
        }
        disabled={editorState.getRedoStack().size === 0}
        title={
          editorState.getRedoStack().size === 0 ? "Nothing to redo" : "Redo"
        }
      >
        <FontAwesomeIcon icon={faRepeat} />
      </button>
      {" | "}
      <button
        type="button"
        className="btn btn-outline-secondary border-0 btn-sm"
        tabIndex={-1}
        onClick={insertWithoutFormat}
        title="Paste without formatting"
      >
        <FontAwesomeIcon icon={faPaste} />
      </button>
      {" | "}
      <button
        type="button"
        className="btn btn-outline-secondary border-0 btn-sm"
        onClick={insertImage}
        title="Insert an image"
      >
        <FontAwesomeIcon icon={faImage} />
      </button>
    </div>
  );
}

EditorButtons.propTypes = {
  editorState: PropTypes.instanceOf(EditorState).isRequired,
  setEditorState: PropTypes.func.isRequired,
};

export default EditorButtons;
